/**
 * CustomerCampaignsTab - Histórico de campanhas disparadas para o cliente/contato
 *
 * Aba carregada SOB DEMANDA: a consulta só acontece quando a aba está ativa,
 * mantendo o custo de abertura do modal inalterado.
 *
 * Fonte dos dados: RPC get_contact_campaign_history (derivada de campaign_recipients,
 * com o conteúdo real da mensagem enviada via join em messages).
 *
 * @version 3.1.0
 * RELEASE: 2026-08-09 v3.1.0 - Clique na campanha abre o CampaignDetailsModal
 *          (antes navegava para a página, tirando o usuário do modal do cliente)
 * RELEASE: 2026-08-09 v3.0.0 - Scroll infinito (10 em 10) via IntersectionObserver,
 *          painel com fundo estilo WhatsApp (papel de parede + tema claro/escuro) e
 *          exibição da inbox usada em cada disparo.
 * RELEASE: 2026-08-09 v2.0.0 - Layout em formato de conversa (balões estilo WhatsApp),
 *          separadores de data e conteúdo fiel do disparo (mídia, template, botões).
 * RELEASE: 2026-08-09 v1.0.0 - Criação inicial
 */
import React, { useState } from 'react';
import { format, isToday, isYesterday } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Megaphone, AlertCircle, ExternalLink, Inbox as InboxIcon, Loader2 } from 'lucide-react';
import { useTheme } from 'next-themes';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { useContactCampaignHistory, ContactCampaignHistoryItem } from '@/hooks/useContactCampaignHistory';
import { CampaignMessageBubble } from './CampaignMessageBubble';
// v3.1.0 — modal de detalhes da campanha (mesmo conteúdo da página)
import { CampaignDetailsModal } from '@/components/tenant/campaigns/CampaignDetailsModal';

// Props do componente
interface CustomerCampaignsTabProps {
  /** ID do cliente (quando existe cadastro) */
  customerId?: string | null;
  /** ID do contato (modo contato, sem cliente vinculado) */
  contactId?: string | null;
  /** Indica se a aba está ativa (controla o lazy loading) */
  isActive: boolean;
}

/** Rótulo amigável para o separador de data */
function dayLabel(value: string) {
  const date = new Date(value);
  if (isToday(date)) return 'Hoje';
  if (isYesterday(date)) return 'Ontem';
  return format(date, "dd 'de' MMMM 'de' yyyy", { locale: ptBR });
}

/** Chave de agrupamento por dia */
function dayKey(value: string) {
  return format(new Date(value), 'yyyy-MM-dd');
}

/**
 * Papel de parede da conversa (mesmo ambiente visual do chat do sistema).
 * Padrão SVG inline em baixa opacidade — sem asset externo.
 */
function useWallpaperStyle(): React.CSSProperties {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const dot = isDark ? '%23ffffff' : '%23000000';
  const pattern = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='56' height='56' viewBox='0 0 56 56'%3E%3Cg fill='${dot}' fill-opacity='0.035'%3E%3Ccircle cx='8' cy='8' r='1.5'/%3E%3Ccircle cx='36' cy='22' r='1.5'/%3E%3Ccircle cx='20' cy='44' r='1.5'/%3E%3Ccircle cx='48' cy='50' r='1.5'/%3E%3C/g%3E%3C/svg%3E")`;

  return {
    backgroundColor: isDark ? '#0b141a' : '#f0ebe3',
    backgroundImage: pattern,
  };
}

/**
 * Lista os disparos de campanha em formato de conversa (balões de saída).
 */
export function CustomerCampaignsTab({ customerId, contactId, isActive }: CustomerCampaignsTabProps) {
  // v3.1.0 — campanha aberta no modal de detalhes
  const [detailsCampaignId, setDetailsCampaignId] = useState<string | null>(null);
  const wallpaper = useWallpaperStyle();

  const {
    data,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useContactCampaignHistory({ customerId, contactId }, { enabled: isActive });

  // Achata as páginas em uma única lista (mais recentes primeiro)
  const items: ContactCampaignHistoryItem[] = React.useMemo(
    () => (data?.pages ?? []).flat(),
    [data]
  );

  // Agrupa os disparos por dia, preservando a ordem
  const groups = React.useMemo(() => {
    const acc: Array<{ key: string; label: string; items: ContactCampaignHistoryItem[] }> = [];
    for (const item of items) {
      const key = dayKey(item.sent_at);
      const last = acc[acc.length - 1];
      if (last && last.key === key) {
        last.items.push(item);
      } else {
        acc.push({ key, label: dayLabel(item.sent_at), items: [item] });
      }
    }
    return acc;
  }, [items]);

  // ---------------------------------------------------------------------------
  // Scroll infinito: sentinela observada no fim da lista
  // O container de scroll é o do modal, por isso root = null + rootMargin
  // ---------------------------------------------------------------------------
  const sentinelRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasNextPage || !isActive) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { root: null, rootMargin: '200px 0px' }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, isActive, items.length]);

  /** Moldura da conversa (mantida em todos os estados) */
  const Frame = ({ children }: { children: React.ReactNode }) => (
    <div
      className="rounded-xl border border-border/60 shadow-inner overflow-hidden"
      style={wallpaper}
    >
      <div className="p-3 md:p-4">{children}</div>
    </div>
  );

  // Estado de carregamento inicial
  if (isLoading) {
    return (
      <Frame>
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex justify-end">
              <Skeleton className="h-16 w-2/3 rounded-lg" />
            </div>
          ))}
        </div>
      </Frame>
    );
  }

  // Erro na consulta
  if (isError) {
    return (
      <Frame>
        <div className="flex flex-col items-center justify-center py-12 gap-2 text-muted-foreground">
          <AlertCircle className="h-6 w-6" />
          <p className="text-sm">Não foi possível carregar o histórico de campanhas.</p>
        </div>
      </Frame>
    );
  }

  // Nenhum disparo encontrado
  if (items.length === 0) {
    return (
      <Frame>
        <div className="flex flex-col items-center justify-center py-12 gap-2 text-muted-foreground">
          <Megaphone className="h-7 w-7 opacity-40" />
          <p className="text-sm">Nenhuma campanha disparada para este contato.</p>
        </div>
      </Frame>
    );
  }

  return (
    <Frame>
      <div className="space-y-4">
        {groups.map((group) => (
          <div key={group.key} className="space-y-3">
            {/* Separador de data flutuante (padrão WhatsApp) */}
            <div className="flex justify-center">
              <span className="rounded-full bg-background/80 backdrop-blur-sm px-3 py-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground shadow-sm">
                {group.label}
              </span>
            </div>

            {group.items.map((item) => (
              <div key={item.id} className="space-y-1">
                {/* Cabeçalho discreto: campanha + canal + inbox de origem */}
                <div className="flex items-center justify-end gap-1.5 pr-1 flex-wrap">
                  <Megaphone className="h-3 w-3 text-amber-600 shrink-0" />
                  <button
                    type="button"
                    onClick={() => setDetailsCampaignId(item.campaign_id)}
                    className="group inline-flex items-center gap-1 text-[11px] font-medium text-muted-foreground hover:text-foreground transition-colors max-w-[60%] truncate"
                  >
                    <span className="truncate">{item.campaign_name ?? 'Campanha'}</span>
                    <ExternalLink className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                  </button>

                  {item.channel_type && (
                    <span className="text-[10px] uppercase text-muted-foreground/70">
                      {item.channel_type}
                    </span>
                  )}

                  {/* Inbox usada no disparo (quando registrada) */}
                  {item.inbox_name && (
                    <span
                      className="inline-flex items-center gap-1 max-w-[45%] rounded-full bg-background/70 px-1.5 py-0.5 text-[10px] text-muted-foreground shadow-sm"
                      title={item.inbox_name}
                    >
                      <InboxIcon className="h-3 w-3 shrink-0" />
                      <span className="truncate">{item.inbox_name}</span>
                    </span>
                  )}
                </div>

                {/* Balão com o conteúdo real enviado */}
                <CampaignMessageBubble item={item} />

                {/* Falha no envio */}
                {item.error_message && (
                  <div className={cn('flex justify-end')}>
                    <p className="text-[11px] text-destructive max-w-[70%] text-right">
                      {item.error_message}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        ))}

        {/* Sentinela do scroll infinito + indicador de carregamento */}
        <div ref={sentinelRef} className="h-1" />
        {isFetchingNextPage && (
          <div className="flex items-center justify-center gap-2 py-2 text-[11px] text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Carregando disparos...
          </div>
        )}
      </div>

      {/* v3.1.0 — detalhes da campanha em modal (mantém o contexto do cliente) */}
      <CampaignDetailsModal
        campaignId={detailsCampaignId}
        open={!!detailsCampaignId}
        onOpenChange={(open) => !open && setDetailsCampaignId(null)}
      />
    </Frame>
  );
}

